import { Injectable } from '@angular/core';
import { CertificationInterface } from 'src/app/model/certifications/certification-interface';
import { CertificationVariantInterface } from 'src/app/model/certifications/certification-variant-interface';
import { CertificationVariantVersionInterface } from 'src/app/model/certifications/certification-variant-version-interface';
import { CertificationTypes } from 'src/app/model/enums/certificationTypes';
import { Status } from 'src/app/model/enums/status';
import {
    CredentialDefinitionNode,
    IssuerCredentialDefinition,
    JsonObject,
    JsonValue
} from '../models/issuer-api.models';

interface CertificationTemplateNode {
    id?: string;
    name: string;
    type: string;
    children?: CertificationTemplateNode[];
    min?: number;
    max?: number;
}

@Injectable({ providedIn: 'root' })
export class IssuerCertificationMapper {
    toCertification(
        definition: IssuerCredentialDefinition,
        organisationId: number,
        participantContextId: string
    ): CertificationInterface {
        const certificationId = this.syntheticId(
            'certification',
            definition.id
        );
        const variantId = this.syntheticId('variant', definition.id);
        const versionId = this.syntheticId('version', definition.id);
        const schema = this.parseSchema(definition.jsonSchema);
        const description = this.stringProperty(schema, 'description') ?? '';
        const schemaTitle = this.stringProperty(schema, 'title');
        const target =
            definition.jsonSchemaUrl ??
            this.stringProperty(schema, '$id') ??
            undefined;
        const editorShape = this.toEditorShape(definition.jsonSchema);

        const version: CertificationVariantVersionInterface = {
            id: versionId,
            certificationVariantId: variantId,
            name: schemaTitle ?? 'Version 1',
            description,
            status: Status.ACTIVE,
            keywords: [],
            attributeValidationValues: {},
            ptgVersions: [],
            ptgVersionIds: [],
            validity: definition.validity,
            shape: editorShape,
            target
        };

        const variant: CertificationVariantInterface = {
            id: variantId,
            certificationId,
            name: schemaTitle ?? `${definition.credentialType} Schema`,
            description,
            certificationVariantVersions: [version]
        };

        return {
            id: certificationId,
            company: organisationId,
            name: definition.credentialType,
            description,
            category: 'Software',
            certificationType: CertificationTypes.ONTOLOGY_CERT,
            validity: definition.validity,
            publishDate: this.toIsoDate(definition.createdAt),
            certificationVariants: [variant],
            galleryPictureIds: [],
            documentIds: [],
            galleryFileIds: [],
            documentFileIds: [],
            listingPkgName: null,
            totalNumberOfProducts: 0,
            shape: definition.jsonSchema,
            target,
            issuerManaged: true,
            issuerParticipantContextId: participantContextId,
            issuerCredentialDefinitionId: definition.id,
            issuerCredentialFormat: definition.format,
            issuerAttestationIds: [...(definition.attestations ?? [])]
        };
    }

    /** Converts the legacy BIMSWARM template tree into Issuer API nodes. */
    toCredentialDefinitionNodes(shapeJson: string): CredentialDefinitionNode[] {
        const parsed = JSON.parse(shapeJson) as JsonValue;
        if (!Array.isArray(parsed)) {
            throw new Error('The BIMSWARM certificate shape must be an array.');
        }
        return (parsed as unknown as CertificationTemplateNode[]).map(
            (node, index) => this.toCredentialDefinitionNode(node, index)
        );
    }

    /** Converts the edited BIMSWARM node tree back to wallet JSON Schema. */
    toJsonSchema(
        shapeJson: string,
        title: string,
        description?: string
    ): string {
        const nodes = this.toCredentialDefinitionNodes(shapeJson);
        const schema: JsonObject = {
            $schema: 'https://json-schema.org/draft/2020-12/schema',
            title,
            type: 'object',
            properties: nodes.reduce((properties, node) => {
                properties[node.label] = this.nodeJsonSchema(node);
                return properties;
            }, {} as JsonObject),
            additionalProperties: false
        };
        if (description?.trim()) {
            schema['description'] = description.trim();
        }
        return JSON.stringify(schema);
    }

    private toEditorShape(jsonSchema: string): string {
        const schema = this.parseSchema(jsonSchema);
        if (!schema) {
            return '[]';
        }
        const properties = schema['properties'];
        if (!this.isJsonObject(properties)) {
            return '[]';
        }
        return JSON.stringify(
            Object.entries(properties).map(([name, property], index) =>
                this.toTemplateNode(name, property, `${index}`)
            )
        );
    }

    private toTemplateNode(
        name: string,
        value: JsonValue,
        path: string
    ): CertificationTemplateNode {
        const schema = this.isJsonObject(value) ? value : {};
        const type = schema['type'];
        const properties = schema['properties'];
        const isObject = type === 'object' || this.isJsonObject(properties);
        const node: CertificationTemplateNode = {
            id: `issuer-schema-${path}`,
            name,
            type:
                isObject
                    ? 'object'
                    : type === 'number' || type === 'integer'
                      ? 'number'
                      : 'string',
            children: []
        };
        const minimum = schema['minimum'] ?? schema['minLength'];
        const maximum = schema['maximum'] ?? schema['maxLength'];
        if (typeof minimum === 'number') node.min = minimum;
        if (typeof maximum === 'number') node.max = maximum;
        if (this.isJsonObject(properties)) {
            node.children = Object.entries(properties).map(
                ([childName, child], index) =>
                    this.toTemplateNode(childName, child, `${path}-${index}`)
            );
        }
        return node;
    }

    private toCredentialDefinitionNode(
        node: CertificationTemplateNode,
        sortOrder: number
    ): CredentialDefinitionNode {
        const type = node.type?.toLowerCase();
        const result: CredentialDefinitionNode = {
            id: node.id,
            label: node.name,
            nodeType:
                type === 'object'
                    ? 'OBJECT'
                    : type === 'number' || type === 'integer'
                      ? 'NUMBER'
                      : 'STRING',
            sortOrder,
            children: (node.children ?? []).map((child, index) =>
                this.toCredentialDefinitionNode(child, index)
            )
        };
        if (result.nodeType === 'STRING') {
            result.minLength = node.min;
            result.maxLength = node.max;
        } else if (result.nodeType === 'NUMBER') {
            result.minValue = node.min;
            result.maxValue = node.max;
        }
        return result;
    }

    private nodeJsonSchema(node: CredentialDefinitionNode): JsonObject {
        if (node.nodeType === 'OBJECT') {
            return {
                type: 'object',
                properties: (node.children ?? []).reduce(
                    (properties, child) => {
                        properties[child.label] = this.nodeJsonSchema(child);
                        return properties;
                    },
                    {} as JsonObject
                ),
                additionalProperties: false
            };
        }
        if (node.nodeType === 'NUMBER') {
            const schema: JsonObject = { type: 'number' };
            if (node.minValue != null) schema['minimum'] = node.minValue;
            if (node.maxValue != null) schema['maximum'] = node.maxValue;
            return schema;
        }
        const schema: JsonObject = { type: 'string' };
        if (node.minLength != null) schema['minLength'] = node.minLength;
        if (node.maxLength != null) schema['maxLength'] = node.maxLength;
        return schema;
    }

    private parseSchema(jsonSchema: string): JsonObject | null {
        if (!jsonSchema) {
            return null;
        }

        try {
            const parsed = JSON.parse(jsonSchema) as JsonValue;
            return parsed !== null &&
                typeof parsed === 'object' &&
                !Array.isArray(parsed)
                ? parsed
                : null;
        } catch {
            return null;
        }
    }

    private stringProperty(
        object: JsonObject | null,
        property: string
    ): string | null {
        const value = object?.[property];
        return typeof value === 'string' && value.trim()
            ? value.trim()
            : null;
    }

    private isJsonObject(value: JsonValue): value is JsonObject {
        return value != null && typeof value === 'object' && !Array.isArray(value);
    }

    private toIsoDate(timestamp?: number): string | undefined {
        if (!timestamp || !Number.isFinite(timestamp)) {
            return undefined;
        }

        const milliseconds =
            timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
        const date = new Date(milliseconds);
        return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
    }

    /**
     * Existing BIMSWARM routes require numeric identifiers. Negative,
     * deterministic IDs keep issuer projections stable and separate from
     * Product Service's positive database IDs.
     */
    private syntheticId(scope: string, externalId: string): number {
        const value = `${scope}:${externalId}`;
        let hash = 2166136261;
        for (let index = 0; index < value.length; index++) {
            hash ^= value.charCodeAt(index);
            hash = Math.imul(hash, 16777619);
        }
        return -((hash >>> 0) % 2147483646 + 1);
    }
}
